import { Params } from '@angular/router';
import { ActivityEntityName } from '@jms/model';
import { paths } from '@jms/tenant/module.jms.authorisation';

export interface EntityManageRouteContext {
    jobId?: string;
    stageId?: string;
}

export interface EntityRoute {
    routerLink: string[] | null;
    queryParams?: Params;
}

export function buildEntityBoardRoute(entity: ActivityEntityName | undefined): EntityRoute {

    switch (entity) {
        case ActivityEntityName.Job:
        case ActivityEntityName.WorkOrder:
        case ActivityEntityName.Task:
            return routeFromPath(paths.Jobs);

        case ActivityEntityName.Asset:
        case ActivityEntityName.JobAsset:
            return routeFromPath('/assets/customer-assets');

        case ActivityEntityName.Opportunity:
            return routeFromPath(paths.Opportunities);

        case ActivityEntityName.EquipmentSerialised:
            return routeFromPath('/assets/hireable/serialised');

        case ActivityEntityName.EquipmentInternalSerialised:
            return routeFromPath('/assets/internal/serialised');

        case ActivityEntityName.EquipmentVehicle:
            return routeFromPath('/assets/hireable/vehicles');

        case ActivityEntityName.EquipmentInternalVehicle:
            return routeFromPath('/assets/internal/vehicles');

        case ActivityEntityName.QuotePro:
            return routeFromPath(paths.QuotesNew);

        case ActivityEntityName.Invoice:
        case ActivityEntityName.PrepaymentInvoice:
        case ActivityEntityName.CreditNote:
            return routeFromPath(paths.FinanceInvoices);

        case ActivityEntityName.Bill:
        case ActivityEntityName.PrepaymentSupplier:
        case ActivityEntityName.CreditNoteSupplier:
            return routeFromPath(paths.FinanceBills);

        default:
            return emptyRoute();
    }
}

export function buildEntityManageRoute(entity: ActivityEntityName | undefined, id: string | undefined, context: EntityManageRouteContext = {}): EntityRoute {

    if (!id) return emptyRoute();

    switch (entity) {
        case ActivityEntityName.Job:
            return { routerLink: ['/', 'jobs', 'view', id, 'true', 'jobs'] };

        case ActivityEntityName.WorkOrder:
            return context.jobId
                ? { routerLink: ['/', 'jobs', 'view', context.jobId, 'false', 'jobs', id] }
                : emptyRoute();

        case ActivityEntityName.Task:
            if (context.jobId && context.stageId) {
                return { routerLink: ['/', 'jobs', 'view', context.jobId, 'false', 'jobs', context.stageId], queryParams: { tab: 'Tasks' } };
            } else if (context.jobId) {
                return { routerLink: ['/', 'jobs', 'view', context.jobId, 'true', 'jobs'], queryParams: { tab: 'Tasks' } };
            }
            return emptyRoute();

        case ActivityEntityName.Asset:
        case ActivityEntityName.JobAsset:
            return { routerLink: ['/', 'assets', 'customer-assets', id] };

        case ActivityEntityName.Opportunity:
            return { routerLink: ['/', 'sales', 'opportunities', id, 'details'] };

        case ActivityEntityName.EquipmentSerialised:
            return { routerLink: ['/', 'assets', 'hireable', 'serialised', id] };

        case ActivityEntityName.EquipmentInternalSerialised:
            return { routerLink: ['/', 'assets', 'internal', 'serialised', id] };

        case ActivityEntityName.EquipmentVehicle:
            return { routerLink: ['/', 'assets', 'hireable', 'vehicles', id] };

        case ActivityEntityName.EquipmentInternalVehicle:
            return { routerLink: ['/', 'assets', 'internal', 'vehicles', id] };

        case ActivityEntityName.QuotePro:
            return { routerLink: ['/', 'quotes', 'quotes-new', 'manage', id] };

        case ActivityEntityName.Invoice:
        case ActivityEntityName.PrepaymentInvoice:
        case ActivityEntityName.CreditNote:
            return { routerLink: ['/', 'finance', 'invoices', id] };

        case ActivityEntityName.Bill:
        case ActivityEntityName.PrepaymentSupplier:
        case ActivityEntityName.CreditNoteSupplier:
            return { routerLink: ['/', 'finance', 'bills', id] };

        // CRM customer/account detail page: /crm/customers/:id (CRM_MODULE_ROUTES -> buildContactRoute(customers) -> :entityId).
        case ActivityEntityName.Customer:
            return { routerLink: ['/', 'crm', 'customers', id] };

        // General contact detail page: /crm/contacts/:id (buildContactRoute(contacts) -> BasicContact -> :entityId).
        // We route every linked contact to the BasicContact view because the agent typically links a job's
        // customer contact and the contact subtype is not carried on the link.
        case ActivityEntityName.Contact:
            return { routerLink: ['/', 'crm', 'contacts', id] };

        // CRM site detail page: /crm/sites/:id (buildSiteRoute(sites) is registered against SitePro; both Site and Site Pro share this segment).
        case ActivityEntityName.Site:
        case ActivityEntityName.SitePro:
            return { routerLink: ['/', 'crm', 'sites', id] };

        // Purchase order detail page: /finance/purchase-orders/detail/:id (PURCHASE_ORDERS_MODULE_ROUTES -> detail -> PURCHASE_ORDERS_DETAIL_MODULE_ROUTES -> :id).
        case ActivityEntityName.PurchaseOrder:
            return { routerLink: ['/', 'finance', 'purchase-orders', 'detail', id] };

        default:
            return emptyRoute();
    }
}

export function getEntityLinkTypeLabel(type: ActivityEntityName): string {

    switch (type) {
        case ActivityEntityName.Job:
            return 'Job';
        case ActivityEntityName.WorkOrder:
            return 'Stage';
        case ActivityEntityName.Task:
            return 'Task';
        case ActivityEntityName.Asset:
        case ActivityEntityName.JobAsset:
            return 'Asset';
        case ActivityEntityName.Opportunity:
            return 'Opportunity';
        case ActivityEntityName.EquipmentSerialised:
            return 'Equipment (Hireable Serialised)';
        case ActivityEntityName.EquipmentInternalSerialised:
            return 'Equipment (Internal Serialised)';
        case ActivityEntityName.EquipmentVehicle:
            return 'Equipment (Hireable Vehicle)';
        case ActivityEntityName.EquipmentInternalVehicle:
            return 'Equipment (Internal Vehicle)';
        default: return type;
    }
}

function routeFromPath(path: string): EntityRoute {

    return { routerLink: pathToRouterLink(path) };
}

function pathToRouterLink(path: string): string[] {

    return ['/', ...path.replace(/^\//, '').split('/').filter(Boolean)];
}

function emptyRoute(): EntityRoute {

    return { routerLink: null };
}
